import { useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import BannerImageList from './BannerImageList';

import { basicMsg, htmlMsg } from '../../../../actions/swal_msg';
import { normalizeUrl } from '../../../../helpers/admin/adminHelper';
import { uploadBannerImages } from '../../../../actions/admin/banner';

import {
    Button, Card, CardActions, CardContent, CardMedia, Container, DialogActions,
    IconButton, Collapse, ImageList, ImageListItem, Input, InputLabel, Link,
    InputAdornment, FormControl, useMediaQuery
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material/styles';

import { regex_file_ext } from '../../../../constants/regex';

const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    })
}));

const DesktopContainer = ({ banner, setBanner, deletedImages, setDeletedImages }) => {
    const { type, images, backup } = banner;
    
    const matches = useMediaQuery('(max-width: 768.5px)');
    const fileInputRef = useRef();

    const [loading, setLoading] = useState(false);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };
    const handleMouseUpPassword = (event) => {
        event.preventDefault();
    };

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(images);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        setBanner(e => ({
            ...e,
            images: items
        }));
    };
    
    const loadedImages = ({ target }) => {
        let count = images.length; // total de imágenes
        let exceeded = false; // se alcanzó el límite?
        if (count >= 20) {
            basicMsg('No puede agregar más imágenes');
            return;
        }

        if (target.files.length > 0) {
            let txt = '';
            let files = [];

            [...target.files].forEach(element => {
                if (count >= 20) exceeded = true;
                else {
                    const { name, type } = element;
                    // archivo es una imagen compatible
                    if (['image/jpeg', 'image/png', 'image/webp'].includes(type)) {
                        const file_ext = regex_file_ext.exec(name)[1];
                        if (file_ext) { // extensión es válida
                            // mismo archivo ya se ha subido => editar nombre
                            if (images.find(e => e.img.name === name)) {
                                // nuevo nombre de archivo
                                // nombre anterior sin extensión + contador + . + extensión
                                const file_name = `${name.replace(/\.[^/.]+$/, "")}_${count}.${file_ext}`;
                                files.push({
                                    img: new File([element], file_name, { type }), url: '', expanded: true
                                });
                            } else
                                files.push({ img: element, url: '', expanded: true });
                            count++;
                        } else txt += `<li>Extensión de ${name} no válida</li>`;
                    } else txt += `<li>${name} no es compatible</li>`;
                }
            });
            setBanner(e => ({
                ...e, images: [...e.images, ...files]
            }));

            let main_txt = '';
            if (exceeded) main_txt += '<li>Se alcanzó el límite de imágenes (20)</li>';
            main_txt += txt;

            if (main_txt) // mensajes
                htmlMsg(
                    'warning', 'Imágenes',
                    `<div style="text-align: left; padding-left: 0pt;"><ul>${main_txt}</ul></div>`
                );
        }
        fileInputRef.current.value = '';
    };

    // remueve imágenes de producto
    const removeImg = name => () => {
        setBanner(e => ({
            ...e,
            images: e.images.filter(i => (i.img.name !== name))
        }));
    };

    const save = async() => {
        setLoading(true);
        if (
            images.length <= 0 ||
            (images.length > 0 && !images.find(e => e.url === ''))
        )
            await uploadBannerImages(
                { _id: banner[type]._id, type }, setBanner, backup[type],
                images, deletedImages[type], setDeletedImages
            );
        else basicMsg('Ingrese URL de todas las imágenes');
        setLoading(false);
    };

    // mostrar más info de banner
    const handleExpandClick = img => () => {
        setBanner(e => ({
            ...e,
            images: images.map(i => (i.img === img) ? { ...i, expanded: !i.expanded } : i)
        }));
    };

    // cambiar tipo de enlace
    const handleExternalClick = img => () => {
        setBanner(e => ({
            ...e,
            images: images.map(i => (i.img === img) ? { ...i, external: !i.external } : i)
        }));
    };

    // URL de banner
    const handleInputChange = img => ({ target: { value } }) => {
        if (value.length < 1001)
            setBanner(e => ({
                ...e,
                images: images.map(i => (i.img === img) ? { ...i, url: value } : i)
            }));
    };

    return (
        <Container maxWidth='xl'>
            <BannerImageList
                banner={ banner }
                setBanner={ setBanner }
                deletedImages={ deletedImages }
                setDeletedImages={ setDeletedImages }
            />

            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="file">
                    {(provided) => (
                        <ImageList gap={20} cols={ matches ? 2 : 3 }
                            { ...provided.droppableProps }
                            ref={ provided.innerRef }>
                            {
                                images.length > 0 &&
                                images.map(({ img, url, expanded, external }, i) => {
                                    return (
                                        <Draggable key={img.name} draggableId={img.name} index={i}>
                                            {(provided) => (
                                                <Card key={i}
                                                    ref={ provided.innerRef }
                                                    { ...provided.draggableProps }
                                                    { ...provided.dragHandleProps }>
                                                    <CardMedia
                                                        component="img"
                                                        alt={ 'banner-nefropolis' }
                                                        height="140"
                                                        image={ URL.createObjectURL(img) }
                                                    />
                                                    <CardActions>
                                                        <IconButton
                                                            onClick={ removeImg(img.name) }
                                                        >
                                                            <RemoveCircleIcon />
                                                        </IconButton>
                                                        <ExpandMore
                                                            expand={ expanded }
                                                            onClick={handleExpandClick(img)}
                                                            aria-expanded={ expanded }
                                                            aria-label="show more"
                                                        >
                                                            <ExpandMoreIcon />
                                                        </ExpandMore>
                                                    </CardActions>
                                                    <Collapse in={ expanded } timeout="auto" unmountOnExit>
                                                        <CardContent>
                                                            <FormControl fullWidth variant="standard">
                                                                <InputLabel htmlFor="new-adornment-banner">
                                                                    { `URL (${external ? 'externo' : 'local'})` }
                                                                </InputLabel>
                                                                <Input
                                                                    id="new-adornment-banner"
                                                                    value={url}
                                                                    startAdornment={
                                                                        !external &&
                                                                            <InputAdornment position="start">
                                                                                nefropolis.com
                                                                            </InputAdornment>
                                                                    }
                                                                    endAdornment={
                                                                        <InputAdornment position="end">
                                                                            <IconButton
                                                                                aria-label={
                                                                                    external ? 'external url' : 'local url'
                                                                                }
                                                                                onClick={handleExternalClick(img)}
                                                                                onMouseDown={handleMouseDownPassword}
                                                                                onMouseUp={handleMouseUpPassword}
                                                                            >
                                                                                {external ? <LinkOffIcon /> : <LinkIcon />}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    }
                                                                    onChange={ handleInputChange(img) }
                                                                />
                                                            </FormControl>
                                                            <Link href={
                                                                normalizeUrl(`${!external ? `nefropolis.com${url}` : url}`)
                                                            }
                                                                target="_blank" rel="noopener noreferrer">
                                                                Visitar enlace
                                                            </Link>
                                                        </CardContent>
                                                    </Collapse>
                                                </Card>
                                            )}
                                        </Draggable>
                                    );
                                })
                            }
                            {provided.placeholder}
                            <ImageListItem sx={{ height: '250px !important' }} className='uploader'>
                                <input
                                    accept="image/*"
                                    ref={fileInputRef}
                                    type="file"
                                    id="select-image-1"
                                    multiple
                                    name="images"
                                    onChange={loadedImages}
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="select-image-1" style={{ marginBottom: 0 }}>
                                    <IconButton variant="outlined" color="primary" component="span">
                                        <CameraAltIcon />
                                    </IconButton>
                                </label>
                            </ImageListItem>
                        </ImageList>
                    )}
                </Droppable>
            </DragDropContext>
            <DialogActions>
                {
                    loading ?
                        <LoadingButton
                            loading
                            loadingPosition="start"
                            startIcon={<SaveIcon />}
                        >
                            Guardando
                        </LoadingButton>
                    :
                        <Button onClick={ save }>Guardar cambios</Button>
                }
            </DialogActions>
        </Container>
    );
};

export default DesktopContainer;